import dayjs from 'dayjs';
import { useCallback, useState } from 'react';

import { today, weekBeforeToday } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useLazyGetOrganizationsAnalyticsPageQuery,
  useSearchOrganizationsAnalyticsMutation,
} from '@app/services/rtkQuery/organization';

import type {
  FetchOrganizationsAnalyticsProps,
  MainTableRow,
  TableRow,
} from '../types';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';

export const useOrganizationAnalytics = () => {
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<[Dayjs, Dayjs] | null>([
    weekBeforeToday,
    today,
  ]);

  const [searchId, setSearchId] = useState<number | null>(null);
  const [tableData, setTableData] = useState<MainTableRow[]>([]);
  const [total, setTotal] = useState(0);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [searchOrganizationsAnalytics, { isLoading: isSearchLoading }] =
    useSearchOrganizationsAnalyticsMutation();

  const [GetOrganizationsAnalyticsPage, { isLoading: isPageLoading }] =
    useLazyGetOrganizationsAnalyticsPageQuery();

  const isLoading = isSearchLoading || isPageLoading;

  const fetchOrganizationsAnalytics = useCallback(
    async ({ search, period }: FetchOrganizationsAnalyticsProps) => {
      try {
        const response = await searchOrganizationsAnalytics({
          SearchName: search,
          DateFrom: period?.[0]?.toISOString() || '',
          DateTo: period?.[1]?.toISOString() || '',
          Page: 1,
          PageSize: pagination.pageSize,
          ActiveBranchId,
        }).unwrap();

        setSearchId(response.SearchId);

        const mappedOrganizations: MainTableRow[] = response.Organizations.map(
          (organization) => ({
            id: organization.Id,
            name: organization.Name,
            newClients: organization.NewClients,
            revenue: organization.Revenue,
            users: organization.UniqueUsers,
            createdAt: organization.RegistrationDate,
            lastLogin: organization.LastLoginMax,
            uniqueUsersToday: organization.UniqueUsersToday,

            _children:
              organization.Branches?.map((branch) => ({
                id: branch.Id,
                name: branch.Name,
                newClients: branch.NewClients,
                revenue: branch.Revenue,
                users: branch.UsersCount,
                createdAt: branch.RegistrationDate,
                lastLogin: branch.LastLoginMax,
              })) ?? [],
          })
        );

        setTableData(mappedOrganizations);
        setTotal(response.TotalOrganizations);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      searchOrganizationsAnalytics,
      pagination.pageSize,
      ActiveBranchId,
      showNotification,
    ]
  );

  const fetchPage = useCallback(
    async (page: number, pageSize: number) => {
      if (!searchId) return;

      try {
        const response = await GetOrganizationsAnalyticsPage({
          searchId,
          page,
          pageSize,
          ActiveBranchId,
        }).unwrap();

        const mappedOrganizations: MainTableRow[] = response.Organizations.map(
          (organization) => ({
            id: organization.Id,
            name: organization.Name,
            newClients: organization.NewClients,
            revenue: organization.Revenue,
            users: organization.UniqueUsers,
            createdAt: organization.RegistrationDate,
            lastLogin: organization.LastLoginMax,
            uniqueUsersToday: organization.UniqueUsersToday,

            _children:
              organization.Branches?.map((branch) => ({
                id: branch.Id,
                name: branch.Name,
                newClients: branch.NewClients,
                revenue: branch.Revenue,
                users: branch.UsersCount,
                createdAt: branch.RegistrationDate,
                lastLogin: branch.LastLoginMax,
              })) ?? [],
          })
        );

        setTableData(mappedOrganizations);
        setTotal(response.TotalOrganizations);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [GetOrganizationsAnalyticsPage, searchId, ActiveBranchId, showNotification]
  );

  const mainColumns: ColumnsType<MainTableRow> = [
    {
      title: 'Название организации',
      dataIndex: 'name',
      width: '20%',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      width: '8%',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Новых клиентов',
      dataIndex: 'newClients',
      width: '12%',
      sorter: (a, b) => (a.newClients ?? 0) - (b.newClients ?? 0),
    },
    {
      title: 'Выручка',
      dataIndex: 'revenue',
      width: '12%',
      sorter: (a, b) => (a.revenue ?? 0) - (b.revenue ?? 0),
    },
    {
      title: 'Пользователи',
      dataIndex: 'users',
      width: '12%',
      sorter: (a, b) => (a.users ?? 0) - (b.users ?? 0),
    },
    {
      title: 'Дата регистрации',
      dataIndex: 'createdAt',
      width: '14%',
      render: (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '-'),
      sorter: (a, b) =>
        dayjs(a.createdAt || '').unix() - dayjs(b.createdAt || '').unix(),
    },
    {
      title: 'Дата последнего входа',
      dataIndex: 'lastLogin',
      width: '14%',
      render: (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '-'),
      sorter: (a, b) =>
        dayjs(a.lastLogin || '').unix() - dayjs(b.lastLogin || '').unix(),
    },
    {
      title: 'Уникальных пользователей сегодня',
      dataIndex: 'uniqueUsersToday',
      width: '18%',
      sorter: (a, b) => (a.uniqueUsersToday ?? 0) - (b.uniqueUsersToday ?? 0),
    },
  ];

  const childColumns: ColumnsType<TableRow> = mainColumns.map((col) => ({
    ...col,
    sorter: false,
  }));

  const handleReset = () => {
    setSearch('');
    setPeriod([weekBeforeToday, today]);
    fetchOrganizationsAnalytics({
      search: '',
      period: [weekBeforeToday, today],
    });
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchOrganizationsAnalytics({
      search: search,
      period: period,
    });
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({ page, pageSize });
    fetchPage(page, pageSize);
  };

  return {
    search,
    setSearch,
    period,
    setPeriod,
    tableData,
    mainColumns,
    childColumns,
    total,
    isLoading,
    pagination,
    fetchOrganizationsAnalytics,
    handleReset,
    handleSearch,
    handlePaginationChange,
  };
};
